import { Box, Stack, Text, Image } from "grommet";
import React, { useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useQueryNumber, useQuerySwitch } from "../../general/Helpers";
import LiveHub from "../../general/LiveHub";
import { AliasMatchParticipantViewModel } from "../../matches/MatchesStore";
import { AliasTournamentParticipantViewModel, getTournament, TournamentViewModel } from "../TournamentsStore";
import { getNextMatch } from "../LiveStreamComponents";

import { StarrySkyCanvas, ConstellationCanvas } from "../../components/themes/galaxy/GalaxyComponents";
import { TeamViewModel } from "../../teams/TeamsStore";

interface TeamPlayers {
    team: TeamViewModel;
    participants: AliasMatchParticipantViewModel[];
}

const fontSizes = {
    "28px": 2,
    "20px": 4,
    "16px": 6,
    "12px": 8,
};

const findClosestFontSize = (size: number) => {
    let closest = 1000;
    let closestSize = "16px";
    for (let key in fontSizes) {
        let diff = Math.abs(fontSizes[key] - size);
        if (diff < closest) {
            closest = diff;
            closestSize = key;
        }
    }
    return closestSize;
};

const imageHeights = {
    "300px": 2,
    "200px": 4,
    "150px": 6,
    "100px": 8,
};

const findClosestImageHeight = (size: number) => {
    let closest = 1000;
    let closestSize = "25%";
    for (let key in imageHeights) {
        let diff = Math.abs(imageHeights[key] - size);
        if (diff < closest) {
            closest = diff;
            closestSize = key;
        }
    }
    return closestSize;
};

export const TournamentMatchPlayersGalaxyWrapper = () => {
    const [ts, setTs] = React.useState(+Date.now());

    let { id } = useParams<{ id: string }>();
    const [teamOne, setTeamOne] = React.useState({} as TeamPlayers);
    const [teamTwo, setTeamTwo] = React.useState({} as TeamPlayers);
    const [tournament, setTournament] = React.useState({} as TournamentViewModel);

    React.useEffect(() => {
        resolve();
    }, []);

    const matchNumber = useQueryNumber("matchNumber", 0);

    const resolve = () => {
        getTournament(id).then((tournament) => {
            setTournament(tournament);
            let match = getNextMatch(tournament, matchNumber);
            if (match) {
                try {
                    if (match.tournamentID) {
                        let teams =
                            match.participants.map((matchParticipant) => {
                                return {
                                    team: matchParticipant.team,
                                    participants: tournament.participants
                                        ?.filter((tournamentParticipant) => tournamentParticipant.teamID == matchParticipant.teamID)
                                        .flatMap((tournamentParticipant) => {
                                            return tournamentParticipant.aliases?.map(
                                                (tournamentAliasParticipant) =>
                                                    ({
                                                        alias: tournamentAliasParticipant.alias,
                                                        team: matchParticipant.team,
                                                        aliasID: tournamentAliasParticipant.aliasID,
                                                        id: tournamentAliasParticipant.id,
                                                        teamID: matchParticipant.team.id,
                                                        teamName: matchParticipant.team.name,
                                                        participantID: tournamentParticipant.id,
                                                        stats: [],
                                                    } as AliasMatchParticipantViewModel)
                                            );
                                        }),
                                } as TeamPlayers;
                            }) || [];

                        setTeamOne(teams[0]);
                        setTeamTwo(teams[1]);
                    } else {
                        let teams = match.participants.map((matchParticipant) => {
                            return {
                                team: matchParticipant.team,
                                participants: matchParticipant.aliases || [],
                            } as TeamPlayers;
                        });
                        setTeamOne(teams[0]);
                        setTeamTwo(teams[1]);
                    }
                } catch (e) {}
            } else {
            }
            console.log("interactive");
        });
    };

    const useTeamColor = useQuerySwitch("useTeamColor");
    const useTournamentName = useQuerySwitch("showTournamentName");
    const useTeamName = useQuerySwitch("showTeamName");

    return (
        <div style={{ overflow: "hidden", maxHeight: "100vh" }}>
            <LiveHub entities={[id]} handleUpdate={resolve}>
                <Stack guidingChild={1}>
                    <Box>
                        {useTournamentName && (
                            <Text style={{ fontFamily: "Calamity" }} textAlign="center" size="80px">
                                {tournament.name?.toUpperCase()}
                            </Text>
                        )}
                    </Box>
                    <Box justify="evenly" fill>
                        {teamOne.team?.id && <RenderTeamPlayers reverse={false} ts={ts} teamPlayers={teamOne} useTeamColor={useTeamColor} useTeamName={useTeamName}></RenderTeamPlayers>}
                        <Text size="80px" style={{ fontFamily: "Calamity" }} textAlign="center">
                            VS
                        </Text>
                        {teamTwo.team?.id && <RenderTeamPlayers reverse={true} ts={ts} teamPlayers={teamTwo} useTeamColor={useTeamColor} useTeamName={useTeamName}></RenderTeamPlayers>}
                    </Box>
                </Stack>
            </LiveHub>
        </div>
    );
};

const RenderTeamPlayers = ({ ts, teamPlayers, useTeamColor, useTeamName, reverse }) => {
    return (
        <Box fill key={teamPlayers.team.id} justify="evenly" direction="row" background={{ image: `url(${teamPlayers.team.logoUri})`, size: "35%", opacity: 0.05 }}>
            <Stack fill anchor="center">
                <Stack fill anchor="left" guidingChild={1}>
                    <StarrySkyCanvas color={useTeamColor ? teamPlayers.team?.color || "white" : "white"}></StarrySkyCanvas>
                    <Box direction="row" justify="evenly" align="center" style={{ minHeight: "50vh" }}>
                        {teamPlayers.participants.map((participantAlias) => (
                            <Stack key={participantAlias.id} anchor="center">
                                <Box justify="center" align="center" fill>
                                    {participantAlias.alias.profileUri && (
                                        <Image
                                            height={findClosestImageHeight(teamPlayers.participants.length)}
                                            src={participantAlias.alias.profileUri + "?ts=" + ts}
                                            style={{ objectFit: "contain" }}
                                        ></Image>
                                    )}
                                    {!participantAlias.alias.profileUri && (
                                        <Image
                                            height={findClosestImageHeight(teamPlayers.participants.length)}
                                            width={findClosestImageHeight(teamPlayers.participants.length)}
                                            src={"/images/faces/blank.png"}
                                        ></Image>
                                    )}
                                    <Text size={findClosestFontSize(teamPlayers.participants.length)} style={{ fontFamily: "Calamity" }}>
                                        {participantAlias.alias.name.toUpperCase()}
                                    </Text>
                                </Box>
                                <ConstellationCanvas
                                    camera={{ position: [0, 20, 75], fov: 60 }}
                                    color={useTeamColor ? teamPlayers.team?.color || "white" : "white"}
                                    style={{ height: "100vh", width: "40vw", left: "-135px" }}
                                ></ConstellationCanvas>
                            </Stack>
                        ))}
                    </Box>
                </Stack>
                {reverse && useTeamName && (
                    <Text size={"60px"} style={{ fontFamily: "Calamity", position: "relative", bottom: `calc((${findClosestImageHeight(teamPlayers.participants.length)} / 2) + 60px)` }}>
                        {teamPlayers.team.name.toUpperCase()}
                    </Text>
                )}
                {!reverse && useTeamName && (
                    <Text size={"60px"} style={{ fontFamily: "Calamity", position: "relative", top: `calc((${findClosestImageHeight(teamPlayers.participants.length)} / 2) + 60px)` }}>
                        {teamPlayers.team.name.toUpperCase()}
                    </Text>
                )}
            </Stack>
        </Box>
    );
};
